import React, { useState } from 'react';
import styled from 'styled-components';

const StepContainer = styled.div`
  padding: 2rem;
`;

const StepContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  color: #333;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  max-width: 500px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: #333;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: ${props => props.hasError ? '2px solid #ef4444' : '1px solid #ddd'};
  border-radius: 6px;
  font-size: 1rem;
  color: #333;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #e9ecef;
    
    &:hover {
      background: #e9ecef;
      color: #333;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CustomerInfoStep = ({
    customerInfo,
    billingInfo,
    errors,
    onUpdateCustomer,
    onUpdateBilling,
    onNext,
    onPrev,
    onClearError
}) => {
    const [localErrors, setLocalErrors] = useState({});

    const validateAndSubmit = () => {
        const newErrors = {};

        if (!customerInfo.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!customerInfo.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!customerInfo.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        setLocalErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onNext();
        }
    };

    return (
        <StepContainer>
            <StepContent>
                <SectionTitle>Customer Information</SectionTitle>

                <FormGrid>
                    <FormField>
                        <Label>First Name *</Label>
                        <Input
                            type="text"
                            value={customerInfo.firstName}
                            onChange={(e) => onUpdateCustomer({ firstName: e.target.value })}
                            hasError={!!localErrors.firstName}
                            placeholder="Enter your first name"
                        />
                        {localErrors.firstName && (
                            <ErrorMessage>{localErrors.firstName}</ErrorMessage>
                        )}
                    </FormField>

                    <FormField>
                        <Label>Last Name *</Label>
                        <Input
                            type="text"
                            value={customerInfo.lastName}
                            onChange={(e) => onUpdateCustomer({ lastName: e.target.value })}
                            hasError={!!localErrors.lastName}
                            placeholder="Enter your last name"
                        />
                        {localErrors.lastName && (
                            <ErrorMessage>{localErrors.lastName}</ErrorMessage>
                        )}
                    </FormField>

                    <FormField>
                        <Label>Email *</Label>
                        <Input
                            type="email"
                            value={customerInfo.email}
                            onChange={(e) => onUpdateCustomer({ email: e.target.value })}
                            hasError={!!localErrors.email}
                            placeholder="Enter your email address"
                        />
                        {localErrors.email && (
                            <ErrorMessage>{localErrors.email}</ErrorMessage>
                        )}
                    </FormField>

                    <FormField>
                        <Label>Phone (Optional)</Label>
                        <Input
                            type="tel"
                            value={customerInfo.phone}
                            onChange={(e) => onUpdateCustomer({ phone: e.target.value })}
                            placeholder="Enter your phone number"
                        />
                    </FormField>
                </FormGrid>

                <ActionButtons>
                    <Button className="secondary" onClick={onPrev}>
                        Back
                    </Button>
                    <Button className="primary" onClick={validateAndSubmit}>
                        Continue to Payment
                    </Button>
                </ActionButtons>
            </StepContent>
        </StepContainer>
    );
};

export default CustomerInfoStep;
